// Refused board page saves in the admin console (TOM-672, ADR 0395): the wire
// shapes of /v1/admin/neo/page-refusals*, the request paths, and how a row and
// its page are shown. Pure, so it is tested under Node.
//
// A refused page is the owner's content, so every read carries the admin's
// access reason. The path builders give null without one, and the pages send
// nothing then.

export type PageRefusalCheck = 'static' | 'rendered';
export type PageRefusalCode = 'invalid_page' | 'render_unavailable';

export interface PageRefusalProblem {
  field: string;
  message: string;
  key?: string;
}

export interface PageRefusalRow {
  id: string;
  createdAt: string;
  boatId: string;
  boatName: string | null;
  userId: string | null;
  userEmail: string | null;
  /** 'system' is the version 1 page upgrade (TOM-963), which keeps a page version 2 refuses. */
  writer: 'owner' | 'neo' | 'system';
  turnRef: string | null;
  /** Null for a chat page's draft (TOM-870): it has no board. */
  boardSlug: string | null;
  itemId: string | null;
  surface?: 'board' | 'chat';
  check: PageRefusalCheck;
  code: PageRefusalCode;
  refusals: PageRefusalProblem[];
  kept: PageRefusalProblem[];
}

export interface PageRefusalList {
  refusals: PageRefusalRow[];
  nextBefore: string | null;
}

export interface PageRefusalDetail extends PageRefusalRow {
  /** `{ sent }` for a static refusal, `{ spec, size }` for a rendered one. */
  page: Record<string, unknown>;
}

export interface PageRefusalFilter {
  boatId?: string;
  check?: PageRefusalCheck;
  code?: PageRefusalCode;
  /** The `createdAt` of the last row read, for the next page. */
  before?: string;
  limit?: number;
}

export const PAGE_REFUSAL_CHECKS: readonly PageRefusalCheck[] = ['static', 'rendered'];
export const PAGE_REFUSAL_CODES: readonly PageRefusalCode[] = [
  'invalid_page',
  'render_unavailable',
];

/** The reason as the server takes it (3 to 200 characters), or null. */
export function accessReason(reason: string): string | null {
  const r = reason.trim();
  return r.length >= 3 && r.length <= 200 ? r : null;
}

/** The list's path, or null without a usable reason. */
export function pageRefusalsPath(reason: string, filter: PageRefusalFilter = {}): string | null {
  const r = accessReason(reason);
  if (r === null) return null;
  const q = new URLSearchParams({ reason: r });
  if (filter.boatId !== undefined) q.set('boatId', filter.boatId);
  if (filter.check !== undefined) q.set('check', filter.check);
  if (filter.code !== undefined) q.set('code', filter.code);
  if (filter.before !== undefined) q.set('before', filter.before);
  if (filter.limit !== undefined) q.set('limit', String(filter.limit));
  return `/v1/admin/neo/page-refusals?${q.toString()}`;
}

/** One refused save's path, or null without a usable reason. */
export function pageRefusalPath(id: string, reason: string): string | null {
  const r = accessReason(reason);
  if (r === null) return null;
  return `/v1/admin/neo/page-refusals/${encodeURIComponent(id)}?${new URLSearchParams({ reason: r }).toString()}`;
}

const CHECKS: Record<PageRefusalCheck, string> = {
  static: 'Page code',
  rendered: 'Drawn page',
};

const CODES: Record<PageRefusalCode, string> = {
  invalid_page: 'Page not accepted',
  render_unavailable: 'Could not draw the page',
};

export function checkLabel(check: PageRefusalCheck): string {
  return CHECKS[check] ?? check;
}

export function codeLabel(code: PageRefusalCode): string {
  return CODES[code] ?? code;
}

export function writerLabel(writer: PageRefusalRow['writer']): string {
  if (writer === 'system') return 'v1 upgrade';
  return writer === 'neo' ? 'Neo' : 'Owner';
}

/** The problems under each field, fields in the order they first appear. */
export function groupByField(
  problems: readonly PageRefusalProblem[]
): { field: string; messages: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const p of problems) {
    const messages = groups.get(p.field);
    if (messages === undefined) groups.set(p.field, [p.message]);
    else messages.push(p.message);
  }
  return [...groups].map(([field, messages]) => ({ field, messages }));
}

/** The parts of a refused page the detail page shows, each as plain text. */
export interface RefusedPageParts {
  html: string | null;
  css: string | null;
  /** Everything else sent or drawn (edits, readings, size, …), as JSON; null when nothing. */
  rest: string | null;
  /** Set when the page was too big to keep: its size in bytes. */
  truncatedBytes: number | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Split a stored page into its HTML, its CSS and the rest. A static refusal
 * keeps what the write sent (`sent`), a rendered one the spec it drew
 * (`spec`) and its size.
 */
export function refusedPageParts(page: unknown): RefusedPageParts {
  const none: RefusedPageParts = { html: null, css: null, rest: null, truncatedBytes: null };
  if (!isRecord(page)) return none;
  if (page.truncated === true) {
    return { ...none, truncatedBytes: typeof page.bytes === 'number' ? page.bytes : null };
  }
  const body = isRecord(page.sent) ? page.sent : isRecord(page.spec) ? page.spec : {};
  const { html, css, ...others } = body;
  const rest: Record<string, unknown> = { ...others };
  for (const [k, v] of Object.entries(page)) {
    if (k !== 'sent' && k !== 'spec') rest[k] = v;
  }
  return {
    html: typeof html === 'string' ? html : null,
    css: typeof css === 'string' ? css : null,
    rest: Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : null,
    truncatedBytes: null,
  };
}

/**
 * The line shown for a failed read: a fixed line for each answer, never the
 * server's own message.
 */
export function pageRefusalErrorText(status: number | undefined, code: string | undefined): string {
  if (status === 400) {
    return code === 'reason_required'
      ? 'Give a reason of 3 to 200 characters.'
      : 'This link is not valid. Go back to the list and choose the save again.';
  }
  if (status === 401 || status === 403) return 'Your admin sign-in has ended. Sign in again.';
  if (status === 404) return 'There is no such refused save. It may be older than 90 days.';
  if (status === 503) {
    return 'Your reading could not be logged, so nothing is shown. Try again in a moment.';
  }
  return 'The refused saves could not be read. Try again in a moment.';
}

/** `GET /v1/admin/neo/v1-pages` (TOM-963): version 1 pages left, per boat. */
export interface V1PagesLeft {
  boats: { boatId: string; boatName: string | null; boardPages: number; namedPages: number }[];
  boardPages: number;
  namedPages: number;
}

/** The summary line over the count: done, or how many pages are left on how many boats. */
export function v1PagesSummary(left: V1PagesLeft): string {
  const pages = left.boardPages + left.namedPages;
  if (pages === 0) {
    return 'No version 1 pages are left. Every board page is version 2.';
  }
  const boats = left.boats.length;
  return `${pages} version 1 ${pages === 1 ? 'page is' : 'pages are'} left on ${boats} ${
    boats === 1 ? 'boat' : 'boats'
  }. Each deploy tries them again; one still here after a deploy was refused by version 2's check.`;
}
